import type { ContentfulTextColumnsType } from "@/types";
import React, { FC, useRef } from "react";
import { ContentManager } from "./content-manager";
import { AnimatePresence, motion } from "framer-motion";
import { useInView } from "@/hooks/reveal-in-view";
import SplitTextAnimation from "./split-text-animation";

export const TextCols: FC<{
  className?: string;
  headingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  size?: "regular" | "md" | "lg";
  singleCol?: boolean;
  maxTitleWidth?: number;
  label?: string;
  content: ContentfulTextColumnsType;
}> = ({
  className,
  headingLevel,
  size,
  singleCol,
  maxTitleWidth,
  label,
  content,
}) => {
  const HeadingTag = headingLevel ? headingLevel : "h2";

  const labelDelay = label ? 0.1 : 0;
  const titleDelay = labelDelay + 0.1;
  const col1Delay = titleDelay + 0.1;
  const col2Delay = col1Delay + 0.1;

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  const animationVariants = {
    hidden: { y: 10, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  return (
    <div ref={ref}>
      <AnimatePresence>
        <motion.div
          className={`textCols textCols_${size || "regular"} ${
            singleCol ? "textCols_singleCol" : ""
          } ${className ? className : ""}`}
          animate={isInView ? "show" : "hidden"}
          variants={animationVariants}
          transition={{ duration: 1 }}
        >
          {label && (
            <motion.div
              className="textCols--label"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 10, opacity: 0 }}
            >
              {label}
            </motion.div>
          )}
          {content?.title && headingLevel === "h1" && (
            <SplitTextAnimation
              htmlElement={
                <HeadingTag
                  className="textCols--title"
                  style={{
                    maxWidth: maxTitleWidth ? `${maxTitleWidth}px` : "none",
                    opacity: isInView ? 1 : 0,
                  }}
                >
                  {content.title}
                </HeadingTag>
              }
              isInView={isInView}
            />
          )}
          {content?.title && headingLevel !== "h1" && (
            <SplitTextAnimation
              htmlElement={
                <HeadingTag
                  className="textCols--title"
                  style={{
                    maxWidth: maxTitleWidth ? `${maxTitleWidth}px` : "none",
                  }}
                >
                  {content.title}
                </HeadingTag>
              }
              isInView={isInView}
            />
          )}
          <div className="textCols--cols">
            {content?.textCol1 && (
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 10, opacity: 0 }}
                transition={{ delay: col1Delay }}
              >
                <ContentManager
                  content={content.textCol1}
                  className="textCols--col"
                />
              </motion.div>
            )}
            {content?.textCol2 && (
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 10, opacity: 0 }}
                transition={{ delay: col2Delay }}
              >
                <ContentManager
                  content={content.textCol2}
                  className="textCols--col"
                />
              </motion.div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
